import type { WebSocketEvent } from '../types/api';

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 3_000;
const WS_BASE = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

export type EventHandler = (event: WebSocketEvent) => void;
export type CloseHandler = (code: number) => void;

export class JobWebSocket {
  private ws: WebSocket | null = null;
  private retries = 0;
  private closed = false;

  constructor(
    private readonly jobId: string,
    private readonly onEvent: EventHandler,
    private readonly onClose: CloseHandler
  ) {}

  connect(): void {
    const token = localStorage.getItem('access_token') ?? '';
    const url = `${WS_BASE}/ws/jobs/${this.jobId}?token=${encodeURIComponent(token)}`;
    this.ws = new WebSocket(url);

    this.ws.onmessage = (e) => {
      try {
        const event: WebSocketEvent = JSON.parse(e.data);
        this.onEvent(event);
        if (['job_completed', 'job_failed', 'job_cancelled'].includes(event.event_type)) {
          this.closed = true;
          this.onClose(1000);
        }
      } catch {
        // Ignore malformed frames
      }
    };

    this.ws.onerror = () => {
      if (!this.closed) this._retry();
    };

    this.ws.onclose = (e) => {
      if (!this.closed && e.code !== 1000 && this.retries < MAX_RETRIES) {
        this._retry();
      } else {
        this.onClose(e.code);
      }
    };
  }

  private _retry(): void {
    this.retries += 1;
    if (this.retries > MAX_RETRIES) {
      this.onClose(4000);
      return;
    }
    setTimeout(() => {
      if (!this.closed) this.connect();
    }, RETRY_DELAY_MS);
  }

  disconnect(): void {
    this.closed = true;
    this.ws?.close(1000);
  }
}
